import React, { useEffect, useState, useMemo } from "react";
import Header from "@/components/Header";
import HelpFab from "@/components/HelpFab";
import api, { formatApiError } from "@/lib/api";
import useCaPrice from "@/lib/useCaPrice";
import { Plus, Trash2, ArrowDownToLine, AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const STATUSES = ["Completed", "Pending", "Failed"];

export default function Withdrawals() {
  const [items, setItems] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ wallet_id: "", date: new Date().toISOString().slice(0,10), ca_withdrawn: "", ca_sold: "", displayed_ca_price: "", actual_sale_price: "", fees: "0", usdt_received: "", exchange_used: "Coinstore", transaction_hash: "", status: "Completed", notes: "" });
  const { price: liveCaPrice } = useCaPrice();
  useEffect(() => { if (liveCaPrice) setForm((f) => (f.displayed_ca_price ? f : { ...f, displayed_ca_price: Number(liveCaPrice).toFixed(2) })); }, [liveCaPrice]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [w, ws] = await Promise.all([api.get("/withdrawals"), api.get("/wallets")]);
      setItems(w.data); setWallets(ws.data);
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        ca_withdrawn: parseFloat(form.ca_withdrawn),
        ca_sold: form.ca_sold ? parseFloat(form.ca_sold) : null,
        displayed_ca_price: form.displayed_ca_price ? parseFloat(form.displayed_ca_price) : null,
        actual_sale_price: form.actual_sale_price ? parseFloat(form.actual_sale_price) : null,
        fees: parseFloat(form.fees || 0),
        usdt_received: parseFloat(form.usdt_received || 0),
      };
      await api.post("/withdrawals", payload);
      setForm({ ...form, ca_withdrawn: "", ca_sold: "", displayed_ca_price: "", actual_sale_price: "", usdt_received: "", transaction_hash: "", notes: "" });
      fetchAll();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this withdrawal?")) return;
    try { await api.delete(`/withdrawals/${id}`); fetchAll(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const totals = useMemo(() => {
    const ca_withdrawn = items.reduce((a, i) => a + (i.ca_withdrawn || 0), 0);
    const ca_sold = items.reduce((a, i) => a + (i.ca_sold || 0), 0);
    const usdt = items.reduce((a, i) => a + (i.net_usdt || 0), 0);
    const failures = items.filter(i => (i.status || "").toLowerCase() === "failed").length;
    const slippages = items.filter(i => i.slippage_percent !== null && i.slippage_percent !== undefined);
    const avgSlippage = slippages.length ? slippages.reduce((a, i) => a + (i.slippage_percent || 0), 0) / slippages.length : 0;
    return { ca_withdrawn, ca_sold, usdt, failures, avgSlippage };
  }, [items]);

  const recoverySeries = useMemo(() => {
    const sorted = items.slice().sort((a, b) => a.date.localeCompare(b.date));
    let cum = 0;
    return sorted.map((i) => { cum += (i.net_usdt || 0); return { date: i.date, recovered: +cum.toFixed(2) }; });
  }, [items]);

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10">
        <div className="mb-8 fade-up">
          <div className="pill mb-3"><span className="dot" />Recovery ledger</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">Withdrawals &amp; USDT recovery</h1>
          <p className="text-ink-2 mt-2 text-sm">Every CA withdrawal + USDT received. Slippage and failures tracked. This drives your principal recovery KPI.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="kpi-tile" data-testid="kpi-w-count"><div className="kpi-label">Withdrawals</div><div className="kpi-num">{items.length}</div></div>
          <div className="kpi-tile" data-testid="kpi-ca-withdrawn"><div className="kpi-label">CA withdrawn</div><div className="kpi-num">{totals.ca_withdrawn.toFixed(2)}</div></div>
          <div className="kpi-tile" data-testid="kpi-ca-sold"><div className="kpi-label">CA sold</div><div className="kpi-num">{totals.ca_sold.toFixed(2)}</div></div>
          <div className="kpi-tile" data-testid="kpi-usdt-recovered"><div className="kpi-label">USDT recovered (net)</div><div className="kpi-num" style={{ color: "#00863A" }}>${totals.usdt.toLocaleString()}</div></div>
          <div className="kpi-tile" data-testid="kpi-failures"><div className="kpi-label">Failures · avg slippage</div><div className="kpi-num" style={{ color: totals.failures > 0 ? "#DC2626" : "#0F1226" }}>{totals.failures} · {totals.avgSlippage.toFixed(1)}%</div></div>
        </div>

        <div className="glass p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="kpi-label">Cumulative</div>
              <h3 className="font-display text-xl font-semibold tracking-tight">USDT recovery over time</h3>
            </div>
            <span className="chip chip-green"><ArrowDownToLine size={12} /> Recovery</span>
          </div>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={recoverySeries}>
                <defs>
                  <linearGradient id="gradRec" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#00C853" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#00C853" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(15,18,38,0.06)" vertical={false} />
                <XAxis dataKey="date" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ border: "1px solid rgba(15,18,38,0.08)", borderRadius: 12, background: "rgba(255,255,255,0.95)" }} />
                <Line type="monotone" dataKey="recovered" stroke="#00C853" strokeWidth={2.5} dot={{ r: 3, fill: "#fff", stroke: "#00C853", strokeWidth: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass overflow-hidden mb-8">
          <div className="p-5 border-b border-black/5"><h3 className="font-display text-xl font-semibold tracking-tight">Withdrawal entries</h3></div>
          <div className="overflow-x-auto">
            <table className="modern" data-testid="withdrawals-table">
              <thead><tr><th>Date</th><th>Wallet</th><th>CA withdrawn</th><th>CA sold</th><th>Disp. price</th><th>Actual</th><th>Slippage</th><th>Fees</th><th>Net USDT</th><th>Exchange</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan="12" className="text-center py-8 text-ink-3">Loading…</td></tr>
                : items.length === 0 ? <tr><td colSpan="12" className="text-center py-8 text-ink-3" data-testid="withdrawals-empty">No withdrawals yet.</td></tr>
                : items.map((w) => {
                  const wal = wallets.find((x) => x.id === w.wallet_id);
                  const slipBad = w.slippage_percent !== null && w.slippage_percent !== undefined && w.slippage_percent >= 5;
                  return (
                    <tr key={w.id} data-testid={`w-row-${w.id}`}>
                      <td className="num text-xs">{w.date}</td>
                      <td>{wal?.label || "—"}</td>
                      <td className="num">{w.ca_withdrawn?.toFixed(4)}</td>
                      <td className="num">{w.ca_sold ? w.ca_sold.toFixed(4) : "—"}</td>
                      <td className="num">{w.displayed_ca_price ? `$${w.displayed_ca_price.toFixed(2)}` : "—"}</td>
                      <td className="num">{w.actual_sale_price ? `$${w.actual_sale_price.toFixed(2)}` : "—"}</td>
                      <td className="num">{w.slippage_percent !== null && w.slippage_percent !== undefined ? <span className={`chip ${slipBad ? "chip-amber" : "chip-gray"}`}>{w.slippage_percent.toFixed(2)}%</span> : "—"}</td>
                      <td className="num">${(w.fees || 0).toFixed(2)}</td>
                      <td className="num font-semibold" style={{ color: "#00863A" }}>${(w.net_usdt || 0).toFixed(2)}</td>
                      <td className="text-xs">{w.exchange_used || "—"}</td>
                      <td><span className={`chip ${w.status === "Completed" ? "chip-green" : w.status === "Failed" ? "chip-amber" : "chip-gray"}`}>{w.status}</span></td>
                      <td><button onClick={() => remove(w.id)} className="btn-danger"><Trash2 size={13} /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="glass-strong p-6 mb-8">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(0,200,83,0.16)" }}><ArrowDownToLine size={18} style={{ color: "#00863A" }} /></div>
            <div>
              <div className="kpi-label">Log a withdrawal</div>
              <h3 className="font-display text-xl font-semibold tracking-tight">New withdrawal entry</h3>
            </div>
          </div>
          <form onSubmit={add} className="grid md:grid-cols-4 gap-3" data-testid="withdrawal-form">
            <select required value={form.wallet_id} onChange={(e) => setForm({ ...form, wallet_id: e.target.value })} className="input-glass" data-testid="ww-wallet">
              <option value="">Wallet…</option>
              {wallets.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
            </select>
            <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-glass" data-testid="ww-date" />
            <input required type="number" step="0.000001" value={form.ca_withdrawn} onChange={(e) => setForm({ ...form, ca_withdrawn: e.target.value })} placeholder="CA withdrawn" className="input-glass" data-testid="ww-ca" />
            <input type="number" step="0.000001" value={form.ca_sold} onChange={(e) => setForm({ ...form, ca_sold: e.target.value })} placeholder="CA sold to USDT" className="input-glass" data-testid="ww-sold" />
            <input type="number" step="0.01" value={form.displayed_ca_price} onChange={(e) => setForm({ ...form, displayed_ca_price: e.target.value })} placeholder="Displayed CA price" className="input-glass" data-testid="ww-disp" />
            <input type="number" step="0.01" value={form.actual_sale_price} onChange={(e) => setForm({ ...form, actual_sale_price: e.target.value })} placeholder="Actual sale price" className="input-glass" data-testid="ww-actual" />
            <input type="number" step="0.01" value={form.fees} onChange={(e) => setForm({ ...form, fees: e.target.value })} placeholder="Fees" className="input-glass" data-testid="ww-fees" />
            <input type="number" step="0.01" value={form.usdt_received} onChange={(e) => setForm({ ...form, usdt_received: e.target.value })} placeholder="USDT received (gross)" className="input-glass" data-testid="ww-usdt" />
            <input value={form.exchange_used} onChange={(e) => setForm({ ...form, exchange_used: e.target.value })} placeholder="Exchange (Coinstore…)" className="input-glass" data-testid="ww-exchange" />
            <input value={form.transaction_hash} onChange={(e) => setForm({ ...form, transaction_hash: e.target.value })} placeholder="Tx hash" className="input-glass" data-testid="ww-tx" />
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input-glass" data-testid="ww-status">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button type="submit" className="btn-primary" data-testid="ww-add"><Plus size={14} /> Log withdrawal</button>
          </form>
          <div className="mt-4 flex items-start gap-2 text-xs text-ink-3">
            <AlertTriangle size={14} style={{ color: "#B45309" }} className="flex-shrink-0 mt-0.5" />
            <span>If slippage ≥ 5% or status is Failed, a risk alert is auto-created. Net USDT = USDT received − fees.</span>
          </div>
        </div>

        {error && <div className="mt-4 chip chip-amber" data-testid="withdrawal-error">{error}</div>}
      </div>
      <HelpFab />
    </div>
  );
}
